import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
	AtIcon,
	BarcodeIcon,
	CalendarIcon,
	ChevronDownIcon,
	LinkIcon,
	MapPinIcon,
	PlusIcon,
	PrintIcon,
	ProgressDeliveredIcon,
	ProgressDispatchedIcon,
	ProgressProcessingIcon,
	TagIcon,
	TargetIcon,
	TruckRightIcon,
} from '../../components/SVGIcons/SVGIcons';
import { dateConverter, isValidURL, parseTrackingStatus, replaceString, sliceString } from '../../helpers';
import { itemListTemp, packLabelTemp } from '../../templates/templates';
import PageWrapper from '../../components/Invoice/PageWrapper';
import { useLocation, useNavigate, useParams } from 'react-router';
import useFetch from '../../hooks/useFetch';
import config from '../../config';
import StopwatchCont from '../../components/ModalListing/StopwatchCont';
import ItemsList from './ItemsList';
import MemberInfo from '../../components/MemberInfo';
import Axios from 'axios';
import Loading from '../../components/Loading/Loading';
import { useModalsGlobal } from '../../hooks/useModalsGlobal';
import { CurrentTeamContext } from '../../contexts/CurrentTeamContext';
import usePost from '../../hooks/usePost';
import { ModalContext } from '../../contexts/ModalContext';
import { RightSidebarContext } from '../../contexts/RightSidebarContext';
import StatusSwitcher from '../../components/TEMPStatusSwitcher';

export default function PackageDetails() {
	const [pack, setPack] = useState({});
	const [loading, setLoading] = useState(true);
	const [reload, setReload] = useState(false);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [addressString, setAddressString] = useState('');

	const { teamState } = useContext(CurrentTeamContext);
	const { rightSidebarDispatch } = useContext(RightSidebarContext);

	let location = useLocation();
	let params = useParams();

	const { showModal } = useModalsGlobal();

	let { flashFadeModal } = useContext(ModalContext);

	let navigate = useNavigate();

	const statuses = {
		PPREPARING: 1,
		IN_TRANSIT: 2,
		DELIVERED: 3,
	};

	const { data, fetch, reqLoading } = useFetch(`${config.apiv1}/package/package.read/${params?.packageUuid}`);

	const { post } = usePost();

	let buildAddressString = () => {
		let recipientName = pack?.address?.name || pack?.address?.teamName || pack?.recipientTeamName;

		let addressString = pack?.address?.addressLine1
			? `${recipientName ? recipientName : ''}${
					pack?.address?.addressLine1 ? `, ${pack?.address?.addressLine1}` : ''
			  }${pack?.address?.county ? `, ${pack?.address?.county}` : ''}${
					pack?.address?.postalCode ? `, ${pack?.address?.postalCode}` : ''
			  }`
			: '';
		setAddressString(addressString);
	};

	async function postPackageStatus(pack, status, message) {
		let res = await post({ packageUuid: pack.packageUuid, status }, `${config.apiv1}/location/location.create`);
		if (res?.data?.status === status) {
			setPack((pack) => ({
				...pack,
				locations: [...pack.locations, res.data],
				last: res.data,
			}));
			flashFadeModal(message);
		}
	}

	function getFilePath(file) {
		let res = Axios({
			url: `${config.api}/file?path=${file.path}`,
			method: 'GET',
			responseType: 'blob',
		}).then((response) => {
			const url = window.URL.createObjectURL(response.data);
			return url;
		});
		return res;
	}

	const toggleDropdown = () => {
		setDropdownOpen(!dropdownOpen);
	};

	const handleQRScan = (pack, teamUuid) => {
		if (teamUuid === pack?.recipientTeamUuid && pack?.last?.status === 2) {
			postPackageStatus(pack, statuses.DELIVERED, 'The package was marked as delivered');
		}
	};

	function downloadPackLabelTemp() {
		packLabelTemp(pack, getFilePath, { x: 20.8, y: 7.5 });
	}

	const downloadItemListTemp = (pack) => {
		itemListTemp(pack, getFilePath, { x: 10, y: 10 + (pack?.items?.length || 0) * 2 });
	};

	async function addItemToPack({ itemUuid, packageUuid }) {
		if (!itemUuid || !packageUuid) {
			return;
		}
		let res = await post(
			{ packageUuid: packageUuid },
			`${config.apiv1}/order/item.update/${itemUuid}`,
			false,
			'PATCH',
			true
		);

		if (res?.data?.ok) {
			setPack((pack) => {
				let items = pack.items || [];
				return {
					...pack,
					items: [...items, res.data?.data],
				};
			});
			flashFadeModal('Item was successfully added to package');
			return true;
		} else {
			flashFadeModal('There was an error when trying to add the item to the package');
			return false;
		}
	}

	function fillAndOpenRightSidebar() {
		const contentType = 7;

		rightSidebarDispatch({ type: 'SET_TEXTBOXES_CLOSED' });
		rightSidebarDispatch({
			type: 'SET_CONTENT_REFERENCE',
			payload: {
				pack,
				immutable: true,
				onDragStartExtended: () => rightSidebarDispatch({ type: 'SET_SIDEBAR_OPEN', payload: false }),
			},
		});
		rightSidebarDispatch({
			type: 'SET_CALLBACK',
			payload: (data) =>
				addItemToPack({ itemUuid: data?.draggableId, packageUuid: data?.destination?.droppableId }),
		});
		rightSidebarDispatch({ type: 'SET_CONTENT_TYPE', payload: contentType });
		rightSidebarDispatch({ type: 'SET_SIDEBAR_OPEN', payload: true });
	}

	useEffect(async () => {
		let mounted = true;
		if (mounted && reload === false) {
			let pack = await fetch(`${config.apiv1}/package/package.read/${params?.packageUuid}`);

			setPack(() => pack);

			setLoading(false);
		} else {
			setLoading(true);
			setReload(false);
		}
		return () => (mounted = false);
	}, [reload]);

	useEffect(async () => {
		let mounted = true;
		let scanned = new URLSearchParams(location.search).get('scanned');

		buildAddressString();

		if (mounted && scanned == 'true') {
			handleQRScan(pack, teamState?.currentTeam?.teamUuid);
		}
		return () => (mounted = false);
	}, [pack, teamState]);

	return (
		<PageWrapper
			header={
				pack
					? {
							icon: <TagIcon />,
							text: 'Package details',
							theme: 'sale',
							button1:
								teamState?.currentTeam?.teamUuid === pack.senderTeamUuid
									? {
											text: 'Edit package',
											cb: () =>
												showModal('editPackage', {
													callback: (e) => {
														if (e?.data) {
															setPack({ ...pack, ...e.data });
														}
													},
													data: pack,
													submitUrl: `${config.apiv1}/package/package.update/${params?.packageUuid}`,
													method: 'PATCH',
												}),
											style: 'secondary',
											width: 140,
									  }
									: undefined,
							// button2: {
							// 	text: 'Change status',
							// 	cb: () =>
							// 		showModal('changePackageStatus', {
							// 			callback: (e) => {
							// 				if (e?.data?.data) {
							// 					setPack((pack) => ({
							// 						...pack,
							// 						locations: [...pack.locations, e.data.data],
							// 					}));
							// 				}
							// 			},
							// 			isSender: teamState?.currentTeam?.teamUuid === pack.senderTeamUuid,
							// 			data: pack,
							// 			submitUrl: `${config.apiv1}/location/location.create`,
							// 			method: 'POST',
							// 		}),
							// 	style: 'secondary',
							// 	width: 140,
							// },
							button3: {
								cb: toggleDropdown,
								style: 'secondary',
								iconLeft: <PrintIcon />,
								iconRight: <ChevronDownIcon />,
								text: 'Print',
								dropdown: {
									open: dropdownOpen,
									items: [
										{
											text: 'Print package label',
											callback: () => {
												toggleDropdown();
												downloadPackLabelTemp();
											},
										},
										{
											text: 'Print shipping slip',
											callback: () => {
												toggleDropdown();
												downloadItemListTemp(pack);
											},
										},
									],
								},
							},
							button4: {
								cb: () => fillAndOpenRightSidebar(),
								style: 'secondary',
								iconRight: <PlusIcon />,
								width: 40,
							},
					  }
					: {}
			}>
			<div className='packageDetails'>
				{!loading ? (
					<>
						<div className='row borderBottom'>
							<div className='marginRight-auto'>
								<h3>{pack?.packageRef}</h3>
							</div>
							{pack?.courier ? <h3>{pack?.courier[0]?.companyName}</h3> : ''}
							<StopwatchCont pack={pack} />
							<StatusSwitcher
								onChange={postPackageStatus}
								resource={pack}
								message={`Successfully updated package status`}
								statuses={[
									{
										key: 'being_prepared',
										label: 'Being prepared',
										icon: ProgressProcessingIcon,
										className: 'status-being-prepared',
										value: 1,
									},
									{
										key: 'in_transit',
										value: 2,
										label: 'In transit',
										icon: ProgressDispatchedIcon,
										className: 'status-in-transit',
									},
									{
										key: 'delivered',
										value: 3,
										label: 'Delivered',
										icon: ProgressDeliveredIcon,
										className: 'status-delivered',
									},
								]}
								value={pack?.last?.status}
								disabledStatuses={
									teamState?.currentTeam?.teamUuid !== pack.senderTeamUuid ? [1, 2] : []
								}
							/>
							{pack?.user ? <MemberInfo user={pack?.user} teamName={pack?.senderTeamName} /> : <></>}
						</div>
						<div className='row'>
							<div className='col'>
								{addressString ? (
									<p>
										<TargetIcon iconClass='dataIcon' />
										{addressString}
									</p>
								) : (
									<></>
								)}
								{pack?.recipient ? (
									<p>
										<AtIcon iconClass='dataIcon' />
										{pack?.recipient}
									</p>
								) : (
									<></>
								)}
							</div>
							{/* <div className='col'>
								<p>
									<MapPinIcon iconClass='dataIcon' />
									{pack?.locations
										? `With ${parseTrackingStatus(
												pack?.locations[pack?.locations?.length - 1]?.status
										  )?.text?.toLocaleLowerCase()}`
										: ''}
								</p>
							</div> */}
							<div className='col'>
								<p>
									<CalendarIcon iconClass='dataIcon' />
									{dateConverter(pack?.quotedCollectionDateTime)}
								</p>
								<p>
									<TruckRightIcon iconClass='dataIcon' />
									{dateConverter(pack?.quotedDeliveryDateTime)}
								</p>
							</div>
							<div className='col'>
								{pack?.trackingNumber && (
									<p>
										<BarcodeIcon iconClass='dataIcon' />
										{pack?.trackingNumber}
									</p>
								)}
								{pack?.courier?.trackingUrl && (
									<a
										href={
											isValidURL(
												replaceString(
													pack?.courier?.trackingUrl,
													'{{trackingNumber}}',
													pack?.trackingNumber
												)
											)
												? replaceString(
														pack?.courier?.trackingUrl,
														'{{trackingNumber}}',
														pack?.trackingNumber
												  )
												: null
										}
										target='_blank'
										rel='noopener noreferrer'>
										<p>
											<LinkIcon iconClass='dataIcon' />
											{sliceString(
												replaceString(
													pack?.courier?.trackingUrl,
													'{{trackingNumber}}',
													pack?.trackingNumber
												),
												0,
												30,
												true
											)}
										</p>
									</a>
								)}
								{pack?.courier?.www && !pack?.courier?.trackingUrl && (
									<a
										href={isValidURL(pack?.courier?.www) ? pack?.courier?.www : null}
										target='_blank'
										rel='noopener noreferrer'>
										<p>
											<LinkIcon iconClass='dataIcon' />
											{pack?.courier?.www}
										</p>
									</a>
								)}
							</div>
						</div>
						<ItemsList listItems={pack?.items} pack={pack} reload={setReload} />
					</>
				) : (
					<Loading type='circle' size='medium' />
				)}
			</div>
		</PageWrapper>
	);
}

{
	/* {pack && (
				<div className='packageDetailsCont'>
					<div className='dataCont'>
						<div className='row'>
							<h4>Location: </h4>
							{pack.locations ? (
								<p>{parseTrackingStatus(currLocation.status).icon}</p>
							) : (
								<p>{parseTrackingStatus(0).icon}</p>
							)}
						</div>
						<div className='row'>
							<h4>Courier: </h4>
							<p>{filterCourier()?.companyName || 'None'}</p>
						</div>
						<div className='row'>
							<h4>Pickup: </h4>
							<p>{dateConverter(pack.quotedCollectionDateTime)}</p>
						</div>
						{currLocation.note === 'Delivered' && (
							<div className='row'>
								<h4>Status: </h4>
								<p>Delivered</p>
							</div>
						)}
						<div className='row marginTop-20'>
							{currLocation.note !== 'Delivered' && (
								<Button
									text='Remove package'
									size='sm'
									style='secondary'
									minWidth={140}
									onClick={() => {
										removePackPrompt(pack);
									}}
								/>
							)}
						</div>
						<div className='row marginTop-20'>
							<Button
								text='PDF'
								size='sm'
								style='secondary'
								minWidth={140}
								iconLeft={<FileIcon />}
								onClick={() => {
									packLabelTemp(pack, getFilePath, { x: 20.8, y: 7.5 });
								}}
							/>
						</div>
					</div>
					<h2>Package List</h2>
					<div className='cols'>
						<h4>Description</h4>
						<h4>Order Ref</h4>
						<h4>Note</h4>
						<h4></h4>
					</div>
					<div className='orderItems'>
						{pack?.items?.length > 0 ? (
							<>
								{pack.items.map((item, key) => {
									return (
										<div className='row' key={key}>
											<p>{item.itemDesc}</p>
											<p>{item.orderRef}</p>
											<p>{item.note}</p>
											<div className='btnRow'>
												<FileIcon
													onClick={() => {
														itemLabelTemp(item, getFilePath, true, { x: 6.5, y: 3.5 });
													}}
													iconClass='orderPageDiscIcon'
												/>
												{!currLocation.note === 'Delivered' && (
													<button
														className='btn3-secondary marginLeft-20'
														onClick={() => {
															removeItemPrompt(item.itemUuid);
														}}>
														Remove
													</button>
												)}
											</div>
										</div>
									);
								})}
							</>
						) : (
							<div className='message'>
								This package has no items. If you want to send something you have to do it from the
								Order Details page through one of your active orders.
							</div>
						)}
					</div>
				</div>
			)} */
}
